/** @format */

import { faExternalLinkAlt } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { api, getApiUrl } from '../../../../api.ts';

const DOC_TYPE_CONFIG = {
  data_sheets: { label: 'Fiches techniques', field: 'data_sheets' },
  warranties: { label: 'Garanties', field: 'warranties' },
  conformity_certificates: {
    label: 'Certificats de conformité',
    field: 'conformity_certificates',
  },
  user_manuals: { label: 'Manuels utilisateur', field: 'user_manuals' },
};

function getFileUrl(item) {
  const url = item?.url;
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const base = getApiUrl().replace(/\/$/, '');
  return `${base}${url.startsWith('/') ? '' : '/'}${url}`;
}

function ProductDocumentsDialog({
  open,
  onClose,
  productId,
  products,
  docType,
  productName,
  zIndex,
}) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);

  const config = DOC_TYPE_CONFIG[docType];

  const productEntries = useMemo(() => {
    if (products?.length) {
      return products.filter((entry) => entry?.id != null);
    }
    if (productId != null) {
      return [{ id: productId, name: productName || '' }];
    }
    return [];
  }, [products, productId, productName]);

  const isMultiProduct = productEntries.length > 1;

  useEffect(() => {
    if (!open || !config || !productEntries.length) return undefined;

    let cancelled = false;
    setLoading(true);
    setGroups([]);

    Promise.all(
      productEntries.map((entry) =>
        api
          .get(`/products/v2/admin-products/${entry.id}/`)
          .then((res) => ({
            productId: entry.id,
            productName: entry.name || `Produit #${entry.id}`,
            items: Array.isArray(res.data[config.field])
              ? res.data[config.field]
              : [],
          }))
          .catch(() => ({
            productId: entry.id,
            productName: entry.name || `Produit #${entry.id}`,
            items: [],
          })),
      ),
    )
      .then((rows) => {
        if (cancelled) return;
        setGroups(rows);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, config, productEntries]);

  const handleOpenFile = (item) => {
    const url = getFileUrl(item);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const totalItems = groups.reduce((sum, group) => sum + group.items.length, 0);

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }
    if (totalItems === 0) {
      return (
        <Typography color="text.secondary">
          Aucun document disponible.
        </Typography>
      );
    }

    if (!isMultiProduct) {
      const items = groups[0]?.items || [];
      return (
        <List dense sx={{ p: 2 }}>
          {items.map((item) => (
            <ListItem
              key={item.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleOpenFile(item)}
                  disabled={!getFileUrl(item)}
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                </IconButton>
              }
            >
              <ListItemText primary={item.name || 'Sans nom'} />
            </ListItem>
          ))}
        </List>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {groups.map((group) => {
          if (!group.items.length) return null;
          return (
            <Box
              key={group.productId}
              sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 700, mb: 0.75, color: 'text.primary' }}
              >
                {group.productName}
              </Typography>
              <List dense disablePadding>
                {group.items.map((item) => (
                  <ListItem
                    key={`${group.productId}-${item.id}`}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleOpenFile(item)}
                        disabled={!getFileUrl(item)}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={item.name || 'Sans nom'} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>
    );
  };

  const dialogTitle = isMultiProduct
    ? `${config?.label} — ${productName || 'Kit'}`
    : `${config?.label} - ${productName || `Produit #${productId}`}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={zIndex != null ? { zIndex } : undefined}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>{renderContent()}</DialogContent>
    </Dialog>
  );
}

ProductDocumentsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  productId: PropTypes.number,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string,
    }),
  ),
  docType: PropTypes.oneOf([
    'data_sheets',
    'warranties',
    'conformity_certificates',
    'user_manuals',
  ]).isRequired,
  productName: PropTypes.string,
  zIndex: PropTypes.number,
};

ProductDocumentsDialog.defaultProps = {
  productId: null,
  products: null,
  productName: '',
  zIndex: null,
};

export default ProductDocumentsDialog;
